import { useState, useCallback } from 'react';
import { FileDown, FileText, Eye } from 'lucide-react';
import { downloadOrderFile } from '../../api/orders';
import DocumentPreviewModal from './DocumentPreviewModal';

interface OrderDocumentsProps {
  orderId: string;
  response?: {
    payload: Record<string, unknown>;
  };
}

interface Report {
  documents?: Array<{
    fileName: string;
    contentType: string;
  }>;
}

export default function OrderDocuments({ orderId, response }: OrderDocumentsProps) {
  const reports = (response?.payload?.reports as Report[]) || [];
  const documents = reports.flatMap((r) => r.documents || []);

  const [preview, setPreview] = useState<{
    fileName: string;
    contentType: string;
    blobUrl: string | null;
    loading: boolean;
  } | null>(null);

  const handleDownload = async (fileName: string) => {
    try {
      const blob = await downloadOrderFile(orderId, fileName);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handlePreview = useCallback(async (fileName: string, contentType: string) => {
    setPreview({ fileName, contentType, blobUrl: null, loading: true });
    try {
      const blob = await downloadOrderFile(orderId, fileName);
      const url = URL.createObjectURL(blob);
      setPreview({ fileName, contentType, blobUrl: url, loading: false });
    } catch (err) {
      console.error('Preview failed:', err);
      setPreview((prev) => prev ? { ...prev, loading: false } : null);
    }
  }, [orderId]);

  const closePreview = useCallback(() => {
    if (preview?.blobUrl) {
      URL.revokeObjectURL(preview.blobUrl);
    }
    setPreview(null);
  }, [preview]);

  if (documents.length === 0) {
    return <p className="text-sm text-gray-400 dark:text-gray-500">No documents available.</p>;
  }

  return (
    <>
      <div className="space-y-2">
        {documents.map((doc, idx) => (
          <div
            key={`${doc.fileName}-${idx}`}
            className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          >
            <div className="flex items-center gap-3 min-w-0">
              <FileText className="h-5 w-5 text-gray-400 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{doc.fileName}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">{doc.contentType}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => handlePreview(doc.fileName, doc.contentType)}
                className="btn-icon"
                title="Preview"
              >
                <Eye className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDownload(doc.fileName)}
                className="btn-icon"
                title="Download"
              >
                <FileDown className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {preview && (
        <DocumentPreviewModal
          open={true}
          fileName={preview.fileName}
          contentType={preview.contentType}
          blobUrl={preview.blobUrl}
          loading={preview.loading}
          onClose={closePreview}
          onDownload={() => handleDownload(preview.fileName)}
        />
      )}
    </>
  );
}
